import { RegisterComponent } from '@/common/register-component';
import { NylasSchedulerConfigConnector } from '@/connector/nylas-scheduler-config-connector';
import { debug } from '@/utils/utils';
import { AttachInternals, Component, Host, Prop, State, Watch, h, Element, Listen, EventEmitter } from '@stencil/core';
import { NylasSchedulerEditor } from '../nylas-scheduler-editor/nylas-scheduler-editor';
import { Event } from '@stencil/core';
import { AdditionalParticipant, Calendar, Configuration, ThemeConfig } from '@nylas/core';
import { User } from '@/common/nylas-api-request';
import i18next from '@/utils/i18n';
import { LANGUAGE_CODE } from '@/common/constants';

type Participant = {
  name?: string;
  email: string;
  grant_id?: string;
  is_valid?: boolean;
  is_organizer?: boolean;
  availability?: Availability;
  booking?: Booking;
};
type Availability = {
  calendar_ids: string[];
};
type Booking = {
  calendar_id: string;
};

/**
 * The `nylas-additional-participants` component is a form input for adding additional participants to an event.
 *
 * @part nadtnlpart__title - The title of the component.
 * @part nadtnlpart__subtitle - The subtitle of the component.
 * @part nadtnlpart__content - The content of the component.
 * @part nadtnlpart__input_group - The input group of the component.
 * @part nadtnlpart__input_wrapper - The input wrapper of the component.
 * @part nadtnlpart__input - The input of the component.
 * @part nadtnlpart__remove-participant - The remove participant button of the component.
 * @part nadtnlpart__add-participant - The add participant button of the component.
 *
 */
@Component({
  tag: 'nylas-additional-participants',
  styleUrl: 'nylas-additional-participants.scss',
  shadow: true,
  formAssociated: true,
})
export class NylasAdditionalParticipants {
  @Element() host!: HTMLNylasAdditionalParticipantsElement;
  /**
   * @standalone
   * The name of the component
   */
  @Prop() name: string = 'participants';
  /**
   * @standalone
   * The selected config
   */
  @Prop() selectedConfiguration?: Configuration;
  /**
   * @standalone
   * The logged in user
   */
  @Prop() currentUser?: User;
  /**
   * @standalone
   * The list of user's calendars.
   */
  @Prop() currentUserCalendars?: Calendar[];
  /**
   * @standalone
   * The additional participants options from the config
   */
  @Prop() participantOptions?: AdditionalParticipant[];
  /**
   * @standalone
   * The event participants
   */
  @Prop() eventParticipants?: Participant[];
  /**
   * @standalone
   * If the config is round robin
   */
  @Prop() roundRobinConfig?: boolean;
  /**
   * The selected language.
   */
  @Prop() selectedLanguage?: LANGUAGE_CODE;
  /**
   * @standalone
   * The theme configuration.
   */
  @Prop({ attribute: 'theme-config' }) readonly themeConfig?: ThemeConfig;
  /**
   * @standalone
   * Callback to search participants by query for autocomplete.
   */
  @Prop() participantSearch?: (query: string) => Promise<AdditionalParticipant[]>;

  @AttachInternals() internals!: ElementInternals;
  @State() participants: Participant[] = this.eventParticipants ?? [];
  @State() participantErrors: { [key: string]: string } = {};
  @State() includeOrganizerAsParticipant: boolean = true;
  @State() isRoundRobinConfig: boolean = false;
  @State() error: string = '';
  @State() requiredError: string = '';
  @State() patternError: string = '';
  @State() participantSearchResults: AdditionalParticipant[] = [];
  @State() participantSearchLoading: boolean = false;
  @State() activeSearchInputIndex: number | null = null;

  private participantSearchDebounceTimer?: ReturnType<typeof setTimeout>;
  private readonly PARTICIPANT_SEARCH_DEBOUNCE_MS = 300;

  /**
   * This event is fired when the selected participants change.
   */
  @Event() valueChanged!: EventEmitter<{
    value: string;
    name: string;
  }>;

  /**
   * This event is fired when a participant from search results is selected
   * that is not already in the participantOptions list.
   */
  @Event() participantSearchResultSelected!: EventEmitter<AdditionalParticipant>;

  /**
   * When a name prop is passed, stencil does not automatically set the name attribute on the host element.
   * Since this component is form-associated, the name attribute is required for form submission.
   * This is a workaround to ensure that the name attribute is set on the host element.
   */
  @Watch('name')
  elementNameChangedHandler(newValue: string) {
    debug('nylas-additional-participants', 'elementNameChangedHandler', newValue);
    this.host.setAttribute('name', newValue);
  }

  @Watch('currentUser')
  @Watch('selectedConfiguration')
  selectedConfigurationAndCurrentUserChangedHandler(newValue: unknown, _oldValue: unknown, propName: string) {
    debug('nylas-additional-participants', 'selectedConfigurationAndCurrentUserChangedHandler', newValue, this.currentUser);

    if (propName === 'selectedConfiguration') {
      this.updateState(newValue as Configuration, this.currentUser, false);
    } else if (propName === 'currentUser') {
      this.updateState(this.selectedConfiguration, newValue as User, false);
    }
  }

  @Watch('selectedLanguage')
  selectedLanguageChangedHandler(newVal: LANGUAGE_CODE, oldVal: LANGUAGE_CODE) {
    if (newVal === oldVal) return;
    i18next.changeLanguage(newVal);
    this.requiredError = i18next.t('fieldRequired', { field: i18next.t('email') });
    this.patternError = i18next.t('invalidInputFormat', { field: i18next.t('email') });
    if (this.error) {
      this.error = i18next.t('nylasAdditionalParticipants.error');
    }
  }

  @Watch('themeConfig')
  themeConfigChangedHandler(newVal: ThemeConfig, oldVal: ThemeConfig) {
    if (newVal === oldVal) return;
    this.applyThemeConfig(newVal);
  }

  connectedCallback() {
    debug('nylas-additional-participants', 'connectedCallback');
    // Use queueMicrotask to defer theme application until after React has set props.
    // This fixes the timing issue where connectedCallback fires before React sets themeConfig.
    queueMicrotask(() => {
      if (this.themeConfig) {
        this.applyThemeConfig(this.themeConfig);
        this.host.setAttribute('data-themed', '');
      }
    });
  }

  componentWillLoad() {
    debug('nylas-additional-participants', 'componentWillLoad');
    this.host.setAttribute('name', this.name);
  }

  componentDidLoad() {
    debug('nylas-additional-participants', 'componentDidLoad');
    if (this.selectedConfiguration) {
      this.updateState(this.selectedConfiguration, this.currentUser, false);
    } else if (this.eventParticipants) {
      this.participants = this.eventParticipants || [];
      this.isRoundRobinConfig = this.roundRobinConfig || false;
      this.updateFormValue(true);
    }
  }

  disconnectedCallback() {
    debug('nylas-additional-participants', 'disconnectedCallback');
  }

  private updateState(selectedConfig?: Configuration, currentUser?: User, valueChanged: boolean = true) {
    this.isRoundRobinConfig = selectedConfig?.availability?.availability_rules?.availability_method
      ? selectedConfig?.availability?.availability_rules?.availability_method !== 'collective'
      : false;
    const sourceParticipants = selectedConfig?.participants || this.eventParticipants || [];
    // Shallow-clone to avoid mutating frozen config objects.
    // For round-robin, the API doesn't store is_organizer, so flag the current user as organizer.
    this.participants = sourceParticipants.map(p => {
      if (this.isRoundRobinConfig && currentUser?.email && p.email === currentUser.email) {
        return { ...p, is_organizer: true };
      }
      return { ...p };
    });
    this.includeOrganizerAsParticipant = this.isRoundRobinConfig && this.participants.find(p => p.email === currentUser?.email) ? true : false;
    this.updateOrganizerAsParticipant(valueChanged);
  }

  /**
   * This method is essentially a workaround to check if the internals are available because
   * the unit tests in stencil do not support the internals.
   * @returns boolean
   */
  get isInternalsAvailable() {
    return this.internals !== undefined && typeof this.internals.setFormValue === 'function' && typeof this.internals.setValidity === 'function';
  }

  private getParticipantLookupSources(): AdditionalParticipant[] {
    const fromOptions = this.participantOptions || [];
    const fromSearch = this.participantSearchResults || [];
    const seen = new Set<string>();
    return [...fromSearch, ...fromOptions].filter(p => {
      if (seen.has(p.email)) return false;
      seen.add(p.email);
      return true;
    });
  }

  private getDropdownOptions(index: number): { value: string; label: string }[] {
    const sources = this.participantSearch ? this.getParticipantLookupSources() : this.participantOptions || [];
    return this.getArrayDifference(sources, this.participants, index);
  }

  private async runParticipantSearch(query: string, index: number) {
    if (!this.participantSearch || !query.trim()) {
      this.participantSearchResults = [];
      this.participantSearchLoading = false;
      return;
    }
    this.activeSearchInputIndex = index;
    this.participantSearchLoading = true;
    this.participantSearchResults = [];
    try {
      const results = await this.participantSearch(query.trim());
      if (this.activeSearchInputIndex === index) {
        this.participantSearchResults = results || [];
      }
    } catch (err) {
      debug('nylas-additional-participants', 'participantSearch failed', err);
    } finally {
      if (this.activeSearchInputIndex === index) {
        this.participantSearchLoading = false;
      }
    }
  }

  @Listen('inputOptionChanged')
  onInputOptionChanged(event: CustomEvent<{ value: string; name: string }>) {
    debug('nylas-additional-participants', 'onInputOptionChanged');
    const EMAIL_REGEX = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const index = event.detail.name;
    const value = event.detail.value;
    const lookupSources = this.getParticipantLookupSources();
    const participant = lookupSources.find(p => p.email === value);

    if (this.participantSearch && value) {
      clearTimeout(this.participantSearchDebounceTimer);
      if (participant) {
        // Don't clear participantSearchResults here — this handler fires both when
        // the user types a value that happens to match an existing search result AND
        // when the user clicks a dropdown option. Clearing results on a typed match
        // empties the dropdown while the user is still interacting with it, causing
        // "no results" to appear. The results are naturally replaced on the next search.
        const alreadyInOptions = this.participantOptions?.some(p => p.email === participant.email);
        if (!alreadyInOptions) {
          this.participantSearchResultSelected.emit(participant);
        }
      } else {
        this.participantSearchDebounceTimer = setTimeout(() => {
          this.runParticipantSearch(value, parseInt(String(index), 10));
        }, this.PARTICIPANT_SEARCH_DEBOUNCE_MS);
      }
    }

    this.participants[index].email = value;

    // Check for duplicate emails
    if (value) {
      const duplicateIndex = this.participants.findIndex((p, i) => i !== parseInt(String(index), 10) && p.email === value);
      if (duplicateIndex !== -1) {
        this.isInternalsAvailable && this.internals.setValidity({ customError: true }, i18next.t('nylasAdditionalParticipants.error_duplicate'));
        this.participantErrors[index] = i18next.t('nylasAdditionalParticipants.error_duplicate');
        this.participants[index].is_valid = false;
        this.participants = [...this.participants];
        return;
      }
    }

    if (!participant && this.isRoundRobinConfig) {
      this.isInternalsAvailable && this.internals.setValidity({ customError: true }, 'For round robin configuration, participant should be selected from the dropdown.');
      this.participantErrors[index] = 'For round robin page, participant should be selected from the dropdown.';
      this.participants[index].is_valid = false;
      this.participants = [...this.participants];
      return;
    } else if (!participant && !EMAIL_REGEX.test(value)) {
      if (this.participantSearch && (this.participantSearchLoading || !value.includes('@'))) {
        this.participants = [...this.participants];
        return;
      }
      this.isInternalsAvailable && this.internals.setValidity({ customError: true }, i18next.t('nylasAdditionalParticipants.error_email'));
      this.participantErrors[index] = i18next.t('nylasAdditionalParticipants.error_email');
      this.participants[index].is_valid = false;
      this.participants = [...this.participants];
      return;
    } else {
      this.participantErrors[index] = '';
      this.participants[index].is_valid = true;
      this.isInternalsAvailable && this.internals.setValidity({});
    }
    // Default to the is_primary calendar if available, otherwise first calendar for all participants
    const primaryCalendar = participant?.calendars?.find(calendar => calendar.is_primary);
    this.participants[index].availability =
      participant && primaryCalendar?.id ? { calendar_ids: [primaryCalendar.id] } : participant?.calendars[0]?.id ? { calendar_ids: [participant.calendars[0].id] } : undefined;
    // If round-robin config, set booking calendar to is_primary if available, otherwise first calendar for all participants
    this.participants[index].booking =
      participant && this.isRoundRobinConfig && primaryCalendar?.id
        ? { calendar_id: primaryCalendar.id }
        : this.isRoundRobinConfig && participant?.calendars[0]?.id
          ? { calendar_id: participant.calendars[0].id }
          : undefined;
    this.participants[index].name = participant?.name || value;
    if (participant?.grant_id) {
      this.participants[index].grant_id = participant.grant_id;
    }
    this.participants = [...this.participants];
    this.updateFormValue();
  }

  updateOrganizerAsParticipant(valueChanged: boolean = true) {
    const findCurrentUserInParticipants = this.participants.find(p => p.email === this.currentUser?.email);
    if (this.isRoundRobinConfig && !findCurrentUserInParticipants && this.currentUser?.email) {
      const bookingCalendarDefault = this.currentUserCalendars?.find(calendar => calendar.is_primary)?.id || this.currentUserCalendars?.[0]?.id || 'primary';
      this.participants = [
        {
          name: this.currentUser?.name,
          email: this.currentUser?.email,
          is_organizer: true,
          availability: {
            calendar_ids: [bookingCalendarDefault],
          },
          booking: {
            calendar_id: bookingCalendarDefault,
          },
        },
        ...this.participants,
      ];
    }
    this.updateFormValue(valueChanged);
  }

  addParticipant() {
    debug('nylas-additional-participants', 'addParticipant');
    this.participants = [...this.participants, { name: '', email: '', is_organizer: false }];
    this.updateFormValue();
  }
  removeParticipant(index: number) {
    debug('nylas-additional-participants', 'removeParticipant');
    this.participants = this.participants.filter((_, i) => i !== index);
    this.updateFormValue();
  }

  updateFormValue(valueChanged = true) {
    debug('nylas-additional-participants', 'updateFormValue');
    const participants = this.isRoundRobinConfig ? (this.includeOrganizerAsParticipant ? this.participants : this.participants.filter(p => !p.is_organizer)) : this.participants;
    if (participants.length === 0) {
      this.error = i18next.t('nylasAdditionalParticipants.error');
      this.isInternalsAvailable && this.internals.setValidity({ customError: true }, this.error);
    } else {
      this.isInternalsAvailable && this.internals.setValidity({});
      this.error = '';
      this.isInternalsAvailable && this.internals.setFormValue(JSON.stringify(participants), this.name);
      if (valueChanged) {
        this.valueChanged.emit({ value: JSON.stringify(participants), name: this.name });
      }
    }
  }

  // Filter out the participants that are already added, optionally excluding a specific index
  // (the participant currently being edited) from the duplicate check.
  getArrayDifference(array1: AdditionalParticipant[], array2: Participant[], excludeIndex?: number) {
    const filtered = array1.filter(participant1 => !array2.some((participant2, i) => i !== excludeIndex && participant1.email === participant2.email));
    return filtered.map(participant => {
      return { value: participant.email, label: participant.email };
    });
  }

  applyThemeConfig(themeConfig?: ThemeConfig) {
    if (themeConfig) {
      for (const [key, value] of Object.entries(themeConfig)) {
        this.host.style.setProperty(`${key}`, value);
      }
    }
  }

  @RegisterComponent<NylasAdditionalParticipants, NylasSchedulerConfigConnector, Exclude<NylasSchedulerEditor['stores'], undefined>>({
    name: 'nylas-additional-participants',
    stateToProps: new Map([
      ['schedulerConfig.additionalParticipants', 'participantOptions'],
      ['schedulerConfig.participantSearch', 'participantSearch'],
      ['schedulerConfig.selectedConfiguration', 'selectedConfiguration'],
      ['schedulerConfig.currentUser', 'currentUser'],
      ['schedulerConfig.calendars', 'currentUserCalendars'],
      ['schedulerConfig.selectedLanguage', 'selectedLanguage'],
      ['schedulerConfig.themeConfig', 'themeConfig'],
    ]),
    eventToProps: {
      participantSearchResultSelected: async (event: CustomEvent<AdditionalParticipant>, nylasSchedulerConfigConnector: NylasSchedulerConfigConnector) => {
        const newParticipant = event.detail;
        if (!newParticipant?.email) return;
        const store = nylasSchedulerConfigConnector.schedulerConfigStore;
        const existing = store.state.additionalParticipants || [];
        if (existing.some(p => p.email === newParticipant.email)) return;
        store.set('additionalParticipants', [...existing, newParticipant]);
      },
    },
    fireRegisterEvent: true,
  })
  render() {
    return (
      <Host part="nadtnlpart">
        <nylas-form-card exportparts="nfc, nfc__header" themeConfig={this.themeConfig}>
          <h3 slot="header-title" class="nylas-additional-participants__title" part="nadtnlpart__title">
            {i18next.t('nylasAdditionalParticipants.headerTitle')}
          </h3>
          <p slot="header-subtitle" class="nylas-additional-participants__subtitle" part="nadtnlpart__subtitle">
            {i18next.t('nylasAdditionalParticipants.headerSubTitle')}
          </p>
          <div slot="content" class="nylas-additional-participants__content" part="nadtnlpart__content">
            <div>
              {this.participants.map((participant, index) => {
                return (
                  <div class={'nylas-additional-participants__input_group'} part="nadtnlpart__input_group">
                    {!participant.is_organizer && <label>{`${i18next.t('nylasAdditionalParticipants.participant')} ${index}`}</label>}
                    <div
                      part="nadtnlpart__input_wrapper"
                      class={{
                        'nylas-additional-participants__input_wrapper': true,
                        'nylas-additional-participants__input_wrapper_organizer': participant?.is_organizer === true,
                        'nylas-additional-participants__input_wrapper_invalid': participant.is_valid === false,
                        'error': participant.is_valid === false,
                      }}
                    >
                      <div class={{ 'nylas-additional-participants__input': true, 'organizer': participant?.is_organizer === true }} part="nadtnlpart__input">
                        {participant.is_organizer ? (
                          <input-component
                            class={'label-input'}
                            name={`${index}`}
                            key={index}
                            label={i18next.t('nylasAdditionalParticipants.organizer')}
                            required={false}
                            requiredError={this.requiredError}
                            patternError={this.patternError}
                            readOnly={participant.is_organizer}
                            defaultValue={participant.email}
                            themeConfig={this.themeConfig}
                          >
                            {this.isRoundRobinConfig && (
                              <div class="required-input" slot="additional-input">
                                <input
                                  type="checkbox"
                                  name={`organizer_participant`}
                                  id={`organizer_participant`}
                                  onClick={() => {
                                    this.includeOrganizerAsParticipant = !this.includeOrganizerAsParticipant;
                                    this.updateFormValue();
                                  }}
                                  checked={this.includeOrganizerAsParticipant}
                                />
                                <label htmlFor={`organizer_participant`} aria-label="Include as participant">
                                  <span class="org-as-participant" part="nadtnlpart__org-as-participant">
                                    {i18next.t('nylasAdditionalParticipants.organizerAsParticipant')}
                                  </span>
                                  <tooltip-component id="organizer_participant_tooltip">
                                    <info-icon slot="tooltip-icon" />
                                    <span slot="tooltip-content">
                                      <strong>{i18next.t('nylasAdditionalParticipants.tooltip.includeOrganizer')}:</strong>{' '}
                                      {i18next.t('nylasAdditionalParticipants.tooltip.includeOrganizerDesc')} <br />
                                      <strong>{i18next.t('nylasAdditionalParticipants.tooltip.excludeOrganizer')}:</strong>{' '}
                                      {i18next.t('nylasAdditionalParticipants.tooltip.excludeOrganizerDesc')}
                                    </span>
                                  </tooltip-component>
                                </label>
                              </div>
                            )}
                          </input-component>
                        ) : (
                          <div class="nylas-additional-participants__input-with-loading">
                            <input-dropdown
                              id={`${index}`}
                              name={`${index}`}
                              filterable={!this.participantSearch}
                              inputValue={participant.email}
                              options={this.getDropdownOptions(index)}
                              noResultsText={
                                this.participantSearch &&
                                !this.participantSearchLoading &&
                                this.activeSearchInputIndex === index &&
                                participant.email?.trim() &&
                                this.getDropdownOptions(index).length === 0
                                  ? i18next.t('nylasAdditionalParticipants.noResults')
                                  : undefined
                              }
                              themeConfig={this.themeConfig}
                            />
                            {this.participantSearch && this.participantSearchLoading && this.activeSearchInputIndex === index && (
                              <span class="nylas-additional-participants__loading" part="nadtnlpart__loading" aria-live="polite">
                                {i18next.t('nylasAdditionalParticipants.searching')}
                              </span>
                            )}
                          </div>
                        )}
                        {!participant.is_organizer && (
                          <button onClick={() => this.removeParticipant(index)} part="nadtnlpart__remove-participant">
                            <close-icon />
                          </button>
                        )}
                      </div>
                    </div>
                    {!participant.is_valid && (
                      <p class="nylas-additional-participants__error" part="nadtnlpart__error">
                        {this.participantErrors[index]}
                      </p>
                    )}
                  </div>
                );
              })}
              <p class="nylas-additional-participants__error" part="nadtnlpart__error">
                {this.error}
              </p>
            </div>
            <button class="nylas-additional-participants__add" part="nadtnlpart__add-participant" onClick={() => this.addParticipant()}>
              <add-circle-icon />{' '}
              <span>
                {this.participants.length > 1
                  ? i18next.t('nylasAdditionalParticipants.addParticipant', { context: 'next' })
                  : i18next.t('nylasAdditionalParticipants.addParticipant', { context: 'first' })}
              </span>
            </button>
          </div>
        </nylas-form-card>
      </Host>
    );
  }
}
